import React, { useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  EditableColumnDef,
  EditableTable,
} from "../../../../../../../shared/components/table/inline-edit-table";
import {
  CustomSelect,
  CustomTextField,
} from "../../../../../../../shared/components/inputfields/form-elements";
import { StyledButton } from "../../../../../../../shared/components/buttons/custom-button";
import { ITcComponentRawMaterialInformation } from "../../../../tc-models/tc-req-dto/tc-certified-product-details.req_dto";
import { dtrackItService } from "../../../../tc-service/dtrackit.service";
import { standardCodeFromValue } from "../../incoming-tc-details/incoming-details-apis";
import useNotification from "../../../../../../../shared/hooks/useNotification";
import { useFormActions } from "../../../../../../../shared/hooks/form-builder.hooks";
import { useTransactionCertificateStore } from "../../../../../../../store/transaction-certificates.store";
import { Utills } from "../../../../../../../shared/app-configs/utilit";
import { useApi } from "../../../../../../../shared/hooks/useApi";
import { TCAppConfig } from "../../../../../../../shared/app-configs/tc-app-configs";
import { useParams } from "react-router-dom";
import {
  getComponentRmWeight,
  makeGetRmStandards,
  makeIsCertifiedRM,
} from "./component-payload";
import { filterRmsCodesByStandard } from "../../../../../../../shared/app-configs/rms-standard-mapping";
import {
  ClaimsCategoryLoadState,
  claimsCategoryMembershipError,
  validateClaimsCategoryMembership,
} from "./claims-category.utils";
import { formatTcV4Decimal } from "../../../../tc-v4-decimals";

interface Option {
  value: string;
  label: string;
}

interface ComponentRawMaterialInfoProps {
  formRef: React.MutableRefObject<any>;
  tcStandards?: string[];
  rawMaterialInfoData: ITcComponentRawMaterialInformation[];
  setRawMaterialInfoData: (data: ITcComponentRawMaterialInformation[]) => void;
  componentNetWeight: number; // component net shipping weight (RM allocation base)
}

// Extract the base standard from a claims category value, e.g. "RCS (RCS 100)" -> "RCS".
// Used to enforce "only 1 claims category per standard".
const extractStandard = (code: string): string => {
  if (!code) return "";
  const normalized = code.trim();
  const upper = normalized.toUpperCase();
  if (upper.startsWith("MATERIALS MATTER") || upper.startsWith("MATERIAL MATTER")) return "MMS";
  if (normalized.toLowerCase() === "na") return "na";
  if (normalized.startsWith("RAS")) return "RAS";
  if (normalized.startsWith("RWS")) return "RWS";
  if (normalized.startsWith("GRS")) return "GRS";
  if (normalized.startsWith("RCS")) return "RCS";
  if (normalized.startsWith("OCS")) return "OCS";
  const parts = normalized.split(/[\s-]/);
  return parts[0]?.trim().toUpperCase() || normalized.toUpperCase();
};

const ComponentRawMaterialInfo: React.FC<ComponentRawMaterialInfoProps> = ({
  formRef,
  tcStandards = [],
  rawMaterialInfoData,
  setRawMaterialInfoData,
  componentNetWeight,
}) => {
  const [data, setData] = useState<ITcComponentRawMaterialInformation[]>(
    rawMaterialInfoData || []
  );
  const { notify } = useNotification();
  const { getCurrentFormValues } = useFormActions("Transaction-Details");
  const isReturn = getCurrentFormValues()?.tcIsReturn === "Yes";
  const { setRestrictPopUpSave, setRawMtrlCodes, isRestrictPopUpSave } =
    useTransactionCertificateStore();
  const { TE_ID } = useParams();
  const { executeGet } = useApi();
  const [editingRowIndex, setEditingRowIndex] = useState<number>(-1);
  const [rawMaterials, setRawMaterials] = useState<Option[]>([]);
  const [groupedRawMaterials, setGroupedRawMaterials] = useState<
    { label: string; options: Option[] }[]
  >([]);
  const [allRawMaterials, setAllRawMaterials] = useState<Option[]>([]);
  const [allScRmCodes, setAllScRmCodes] = useState<Option[]>([]);
  const [allRmCodes, setAllRmCodes] = useState<Option[]>([]);
  const [rawMaterialsLoading, setRawMaterialsLoading] = useState<boolean>(false);
  const [certifiedRawMaterialCodes, setCertifiedRawMaterialCodes] = useState<any[]>([]);
  const [tcStandardList, setTcStandardList] = useState<string[]>(tcStandards);
  const [claimsCategoryOptions, setClaimsCategoryOptions] = useState<Option[]>([]);
  const [claimsCategoryLoadState, setClaimsCategoryLoadState] =
    useState<ClaimsCategoryLoadState>("idle");

  const isCertifiedRM = makeIsCertifiedRM(certifiedRawMaterialCodes, tcStandardList);
  const getRmStandards = makeGetRmStandards(certifiedRawMaterialCodes);

  // Claims categories offered for a row are narrowed to the RM's own certified standards
  // (e.g. RM0005 = RCS/GRS -> only RCS/GRS label grades). Falls back to the TC-standard
  // filtered list when the RM↔standard mapping isn't loaded or yields no match, so the
  // dropdown never dead-ends.
  const claimsOptionsForCode = (code: string): Option[] => {
    const standards = getRmStandards(code).map((s) => s.toLowerCase());
    if (!standards.length) return claimsCategoryOptions;

    const tcStdCodes = tcStandardList.map((s) => {
      const code = standardCodeFromValue(s).toLowerCase();
      const raw = String(s || "").toLowerCase();
      if (raw.includes("materials matter") || raw.includes("material matter") || raw.includes("mms")) {
        return "mms";
      }
      return code;
    });
    if (tcStdCodes.includes("mms")) {
      const hasMmsStd = standards.some((std) =>
        ["mms", "ras", "rcs", "grs", "rds", "rms", "rws"].includes(std)
      );
      if (hasMmsStd && !standards.includes("mms")) {
        standards.push("mms");
      }
    }

    const filtered = claimsCategoryOptions.filter((opt) =>
      standards.includes(extractStandard(String(opt.value)).toLowerCase())
    );
    return filtered.length ? filtered : claimsCategoryOptions;
  };

  // Canonical certified resolution: live recompute is source of truth when ready; stored flag fallback.
  const resolveCertified = (code: string, stored: any): boolean => {
    const ready =
      Array.isArray(certifiedRawMaterialCodes) &&
      certifiedRawMaterialCodes.length > 0 &&
      Array.isArray(tcStandardList) &&
      tcStandardList.length > 0;
    return ready ? isCertifiedRM(code) : stored === true;
  };

  // Allocated weight = component net weight * RM% / 100.
  const getRmWeight = (pct: any): number =>
    getComponentRmWeight(componentNetWeight, pct);

  const calculateTotalPercentage = (
    dataArray: ITcComponentRawMaterialInformation[]
  ): number =>
    dataArray.reduce((sum, item) => {
      if (
        item.tcProductComponentRawMaterialCode ||
        Number(item.tcProductComponentRawMaterialPercentage ?? 0) > 0
      ) {
        // The number input persists percentages as strings — coerce before summing,
        // otherwise `+` concatenates ("0" + "40" + 60 → "04060") and every total check fails.
        return sum + (Number(item.tcProductComponentRawMaterialPercentage) || 0);
      }
      return sum;
    }, 0);

  const isTotalOneHundred = (): boolean => calculateTotalPercentage(data) === 100;

  // Claims category validation: required for certified rows + only 1 per standard.
  const validateClaimsCategoriesFor = (
    rows: ITcComponentRawMaterialInformation[]
  ): boolean => {
    const certifiedRows = rows.filter((r) =>
      resolveCertified(r.tcProductComponentRawMaterialCode, r.certified)
    );
    for (const r of certifiedRows) {
      if (!r.claimsCategory || !String(r.claimsCategory).trim()) {
        notify(
          `Claims Category is required for certified raw material ${r.tcProductComponentRawMaterialCode}`,
          "error"
        );
        return false;
      }

      const membership = validateClaimsCategoryMembership(
        r.claimsCategory,
        claimsOptionsForCode(r.tcProductComponentRawMaterialCode).map(
          (option) => option.value
        ),
        claimsCategoryLoadState
      );
      if (!membership.isValid) {
        notify(
          claimsCategoryMembershipError(
            membership.reason,
            r.tcProductComponentRawMaterialCode,
            r.claimsCategory
          ),
          "error"
        );
        return false;
      }
    }
    const byStandard = new Map<string, Set<string>>();
    for (const r of certifiedRows) {
      const std = extractStandard(String(r.claimsCategory || ""));
      if (!std || std.toLowerCase() === "na") continue;
      if (!byStandard.has(std)) byStandard.set(std, new Set());
      byStandard.get(std)!.add(String(r.claimsCategory).trim());
    }
    for (const [std, values] of Array.from(byStandard.entries())) {
      if (values.size > 1) {
        notify(
          `Only 1 claims category per standard is allowed. Conflicting claims categories for standard ${std}.`,
          "error"
        );
        return false;
      }
    }
    return true;
  };

  const validationSchema = Yup.object().shape({
    tcProductComponentRawMaterialCode: Yup.string()
      .required("Component Raw Material Code is required")
      .test(
        "unique-raw-material-code",
        "Component Raw Material Code must be unique",
        function (value) {
          if (!value) return true;
          const isDuplicate = data.some((item, index) => {
            if (index === editingRowIndex) return false;
            return item.tcProductComponentRawMaterialCode === value;
          });
          return !isDuplicate;
        }
      ),
    tcProductComponentRawMaterialPercentage: Yup.number()
      .typeError("Percentage must be a number")
      .required("Percentage is required")
      .positive("Percentage must be positive")
      .max(100, "Percentage cannot exceed 100")
      .test("percentage-max-5-decimals", "Only 5 decimal places allowed", function (value) {
        if (value != null) {
          const decimals = (String(value).split(".")[1] || "").length;
          return decimals <= 5;
        }
        return true;
      })
      .test(
        "percentage-total-not-exceed-100",
        "Total raw material percentage cannot exceed 100%",
        function (value) {
          if (editingRowIndex > -1 && value != null) {
            const total = data.reduce((sum, item, index) => {
              if (index === editingRowIndex) return sum;
              if (
                item.tcProductComponentRawMaterialCode ||
                Number(item.tcProductComponentRawMaterialPercentage ?? 0) > 0
              ) {
                // Coerce: stored percentages can be strings (number input), and string
                // `+` concatenation would inflate the total and reject valid 100% splits.
                return sum + (Number(item.tcProductComponentRawMaterialPercentage) || 0);
              }
              return sum;
            }, 0);
            return total + Number(value) <= 100;
          }
          return true;
        }
      ),
  });

  const formik = useFormik<ITcComponentRawMaterialInformation>({
    initialValues: {
      tcProductComponentRawMaterialCode: "",
      tcProductComponentRawMaterialPercentage: "",
      tcProductComponentRawMaterialCodeDescription: "",
      claimsCategory: "",
      certified: false,
    },
    validationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: () => {},
  });

  useEffect(() => {
    if (formRef) {
      formRef.current = {
        ...formik,
        editingRowIndex,
        hasUnsavedRow: editingRowIndex !== -1,
        validateClaimsCategories: () => validateClaimsCategoriesFor(data),
        // Exposed so the component modal can enforce "RM% sums to 100" before advancing.
        getTotalPercentage: () => calculateTotalPercentage(data),
        getRows: () => data,
      };
    }
  }, [
    formRef,
    formik,
    editingRowIndex,
    data,
    certifiedRawMaterialCodes,
    tcStandardList,
    claimsCategoryOptions,
    claimsCategoryLoadState,
  ]);

  useEffect(() => {
    setData(Array.isArray(rawMaterialInfoData) ? rawMaterialInfoData : []);
  }, [rawMaterialInfoData]);

  // Fetch RM codes (grouped SC vs Other) — identical source to the product RM step.
  useEffect(() => {
    const getRawMaterialCodes = async () => {
      setRawMaterialsLoading(true);
      try {
        const result: any = await dtrackItService.getRawMaterialCodes(String(TE_ID || ""));
        const scRmCodes = Array.isArray(result?.data?.["sc-rmcode"])
          ? result.data["sc-rmcode"].map((t: any) => ({
              value: t.code_1,
              label: `${t.description_1} (${t.code_1})`,
            }))
          : [];
        const rmCodes = Array.isArray(result?.data?.rmcodes)
          ? result.data.rmcodes.map((t: any) => ({
              value: t.code_1,
              label: `${t.description_1} (${t.code_1})`,
            }))
          : [];
        const legacyFlatList =
          !scRmCodes.length && !rmCodes.length && Array.isArray(result?.data)
            ? result.data.map((t: any) => ({
                value: t.code_1,
                label: `${t.description_1} (${t.code_1})`,
              }))
            : [];
        const combined = [...scRmCodes, ...rmCodes, ...legacyFlatList];
        setRawMaterials(combined);
        setGroupedRawMaterials(
          [
            { label: "SC Raw Material Codes", options: scRmCodes },
            { label: "Other Raw Material Codes", options: rmCodes },
          ].filter((group) => group.options.length > 0)
        );
        setRawMtrlCodes(combined);
      } catch (err) {
        Utills.cl(`Error in Fetching Raw Material Codes ${err}`);
        notify("Failed to fetch raw material codes", "error");
      } finally {
        setRawMaterialsLoading(false);
      }
    };
    if (TE_ID) getRawMaterialCodes();
  }, [TE_ID]);

  useEffect(() => {
    const fetchCertifiedRawMaterials = async () => {
      try {
        const response = await executeGet(TCAppConfig.GET_CERTIFIED_RAW_MATERIAL_CODES);
        const list = response?.data?.response || response?.data;
        setCertifiedRawMaterialCodes(Array.isArray(list) ? list : []);
      } catch (error) {
        Utills.cl(`Failed to fetch certified raw materials ${error}`);
      }
    };
    fetchCertifiedRawMaterials();
  }, []);

  useEffect(() => {
    setTcStandardList(Array.isArray(tcStandards) ? tcStandards : []);
  }, [tcStandards]);

  useEffect(() => {
    let active = true;
    const getProductClaimsCategory = async () => {
      setClaimsCategoryOptions([]);
      setClaimsCategoryLoadState("loading");
      try {
        // DAT-501: the Claims Category dropdown lists the label grades that
        // belong to the TC standards (the Seller-SC-approval rule was struck
        // from the validation list).
        const tcStdCodes = tcStandardList
          .map((std: string) => {
            const code = standardCodeFromValue(std).toLowerCase();
            const raw = String(std || "").toLowerCase();
            if (raw.includes("materials matter") || raw.includes("material matter") || raw.includes("mms")) {
              return "mms";
            }
            return code;
          })
          .filter(Boolean);
        const categoryMatchesStd = (category: string, stdCode: string) => {
          const catStd = standardCodeFromValue(category).toLowerCase();
          const targetStd = stdCode.toLowerCase();
          const isMms = targetStd === "mms" || targetStd.includes("materials matter") || targetStd.includes("material matter");
          if (isMms) {
            return ["mms", "ras", "rcs", "grs", "rds", "rms", "rws"].includes(catStd);
          }
          return catStd === targetStd;
        };
        const lg: any = await dtrackItService.getStandradLabelGradeOnBasisOfSelectedStandard(
          tcStdCodes.map((s) => s.toUpperCase())
        );
        const lgList = Array.isArray(lg?.data) ? lg.data : [];
        const categories = Array.from(
          new Set<string>(
            lgList
              .map((item: any) => item?.code_1)
              .filter((code: any): code is string => Boolean(code))
              .filter((code: string) =>
                tcStdCodes.some((stdCode: string) => categoryMatchesStd(code, stdCode))
              )
          )
        );
        if (active) {
          setClaimsCategoryOptions(
            categories.map((category: string) => ({ value: category, label: category }))
          );
          setClaimsCategoryLoadState("loaded");
        }
      } catch (err) {
        Utills.cl(`Error fetching product claims category: ${err}`);
        if (active) {
          setClaimsCategoryOptions([]);
          setClaimsCategoryLoadState("error");
        }
      }
    };
    if (TE_ID && tcStandardList.length > 0) {
      getProductClaimsCategory();
    } else {
      setClaimsCategoryOptions([]);
      setClaimsCategoryLoadState("idle");
    }
    return () => {
      active = false;
    };
  }, [tcStandardList, TE_ID]);

  const displayedRawMaterials: Option[] = rawMaterialsLoading
    ? [{ value: "__loading__", label: "Loading..." }]
    : rawMaterials;

  const displayedGroupedRawMaterials =
    rawMaterialsLoading || groupedRawMaterials.length === 0 ? undefined : groupedRawMaterials;

  const claimsCategoryPlaceholder =
    claimsCategoryLoadState === "loading"
      ? "Loading Claims Categories..."
      : claimsCategoryLoadState === "error"
        ? "Claims Categories Unavailable"
        : "Select Claims Category";

  const handleCellChange = (rowIndex: number, columnId: string, value: any) => {
    formik.setFieldValue(columnId, value);
    setData((prevData) =>
      prevData.map((row, index) =>
        index === rowIndex ? { ...row, [columnId]: value === undefined ? "" : value } : row
      )
    );
  };

  const handleAddNewRow = () => {
    const newRow: ITcComponentRawMaterialInformation = {
      tcProductComponentRawMaterialCode: "",
      tcProductComponentRawMaterialPercentage: "",
      tcProductComponentRawMaterialCodeDescription: "",
      claimsCategory: "",
      certified: false,
    };
    setData((prevData) => [...prevData, newRow]);
    setEditingRowIndex(data.length);
    setRestrictPopUpSave(true);
    formik.setValues(newRow);
    formik.setTouched({});
  };

  const handleCancelEdit = () => {
    if (editingRowIndex === data.length - 1) {
      const cleanData = data.slice(0, -1);
      setData(cleanData);
      setRawMaterialInfoData(cleanData);
    }
    setEditingRowIndex(-1);
    setRestrictPopUpSave(false);
    formik.resetForm();
    (formik as any).hasUnsavedRow = false;
  };

  const handleSaveRow = async (): Promise<boolean> => {
    const errors = await formik.validateForm();
    if (Object.keys(errors).length > 0) {
      Object.values(errors).forEach((err) => notify(err as string, "error"));
      return false;
    }

    const certified = isCertifiedRM(formik.values.tcProductComponentRawMaterialCode);
    const updatedRow: ITcComponentRawMaterialInformation = {
      ...formik.values,
      certified,
      claimsCategory: certified ? formik.values.claimsCategory || "" : "",
    };

    if (certified && (!updatedRow.claimsCategory || !String(updatedRow.claimsCategory).trim())) {
      notify("Claims Category is required for certified raw materials", "error");
      return false;
    }

    const newData = data.map((row, index) =>
      index === editingRowIndex ? updatedRow : row
    );

    if (!validateClaimsCategoriesFor(newData)) return false;

    setData(newData);
    setRawMaterialInfoData(newData);
    setEditingRowIndex(-1);
    setRestrictPopUpSave(false);
    formik.resetForm();
    (formik as any).hasUnsavedRow = false;
    notify("Row saved successfully", "success");
    return true;
  };

  const handleEditRow = (rowIndex: number) => {
    setEditingRowIndex(rowIndex);
    setRestrictPopUpSave(true);
    formik.setValues(data[rowIndex]);
  };

  const handleDeleteRow = (rowIndex: number) => {
    const updatedData = data.filter((_, index) => index !== rowIndex);
    setData(updatedData);
    setRawMaterialInfoData(updatedData);
    notify("Row deleted successfully", "success");
  };

  const columns: EditableColumnDef<ITcComponentRawMaterialInformation>[] = [
    {
      accessorKey: "tcProductComponentRawMaterialCode",
      header: "Component Raw Material Code",
      Header: (
        <span>
          {" "}
          <i style={{ color: "red" }}>* </i>Component Raw Material Code
        </span>
      ),
      renderEditCell: ({ value, onChange, rowIndex }) => (
        <CustomSelect
          label=""
          name="tcProductComponentRawMaterialCode"
          value={value}
          options={displayedRawMaterials}
          groupedOptions={displayedGroupedRawMaterials}
          isOptionDisabled={(option: any) =>
            rawMaterialsLoading || option?.value === "__loading__"
          }
          enableSearch={true}
          placeholder="Select Raw Material Code"
          onChange={(e) => {
            const selectedCode = String(e.target.value);
            const selectedMaterial = rawMaterials.find((mat) => mat.value === selectedCode);
            const description = selectedMaterial ? selectedMaterial.label.split(" (")[0] : "";
            const certified = isCertifiedRM(selectedCode);
            onChange(selectedCode);
            formik.setFieldValue("tcProductComponentRawMaterialCodeDescription", description);
            handleCellChange(rowIndex, "tcProductComponentRawMaterialCodeDescription", description);
            formik.setFieldValue("certified", certified);
            handleCellChange(rowIndex, "certified", certified);
            if (!certified) {
              formik.setFieldValue("claimsCategory", "");
              handleCellChange(rowIndex, "claimsCategory", "");
            }
          }}
        />
      ),
    },
    {
      // AUTO: derived from the certified-RM mapping ∩ TC standards.
      accessorKey: "certified",
      header: "Certified",
      Header: <span>Certified</span>,
      enableEditing: false,
      Cell: ({ row }: any) => {
        const certified = resolveCertified(
          row?.original?.tcProductComponentRawMaterialCode,
          row?.original?.certified
        );
        return <span>{certified ? "Yes" : "No"}</span>;
      },
    },
    {
      accessorKey: "tcProductComponentRawMaterialPercentage",
      header: "Component Raw Material Percentage",
      Header: (
        <span>
          {" "}
          <i style={{ color: "red" }}>* </i>Component Raw Material Percentage
        </span>
      ),
      renderEditCell: ({ value, onChange }) => (
        <CustomTextField
          name="tcProductComponentRawMaterialPercentage"
          value={value}
          inputProps={{ max: 100, step: "0.00001" }}
          type="number"
          onChange={(e) => {
            const raw = e.target.value;
            if (raw === "" || raw === null || raw === undefined) {
              onChange(raw);
              return;
            }
            const matched = String(raw).match(/^\d*(?:\.(\d{0,5})?)?$/);
            const num = Number(raw);
            if (matched && !Number.isNaN(num) && num >= 0 && num <= 100) {
              onChange(raw);
            }
          }}
          placeholder="Enter value (max 5 decimals)"
          sx={{ mb: 2 }}
        />
      ),
    },
    {
      // AUTO: allocated weight = component net * RM% / 100.
      accessorKey: "tcProductComponentRawMaterialWeight",
      header: "Allocated Weight",
      Header: <span>Allocated Weight</span>,
      enableEditing: false,
      Cell: ({ row }: any) => {
        const pct = row?.original?.tcProductComponentRawMaterialPercentage;
        const w = getRmWeight(pct);
        return <span>{pct === undefined || pct === null || pct === "" ? "" : formatTcV4Decimal(w)}</span>;
      },
    },
    {
      // USER input: claims category — dropdown for certified rows, greyed N/A otherwise.
      accessorKey: "claimsCategory",
      header: "Claims Category",
      Header: <span>Claims Category</span>,
      Cell: ({ row }: any) => {
        const certified = resolveCertified(
          row?.original?.tcProductComponentRawMaterialCode,
          row?.original?.certified
        );
        if (!certified) return <span style={{ color: "rgba(0,0,0,0.4)" }}>N/A</span>;
        return <span>{row?.original?.claimsCategory || ""}</span>;
      },
      renderEditCell: ({ value, onChange, rowIndex }) => {
        const code =
          data[rowIndex]?.tcProductComponentRawMaterialCode ||
          formik.values.tcProductComponentRawMaterialCode;
        const certified = resolveCertified(code, data[rowIndex]?.certified);
        if (!certified) {
          return <CustomTextField name="claimsCategory" value="N/A" disabled sx={{ mb: 2 }} />;
        }
        return (
          <CustomSelect
            label=""
            name="claimsCategory"
            value={value || ""}
            options={claimsOptionsForCode(code)}
            disabled={claimsCategoryLoadState !== "loaded"}
            enableSearch={true}
            placeholder={claimsCategoryPlaceholder}
            onChange={(e) => onChange(e.target.value)}
          />
        );
      },
    },
  ];

  return (
    <Box
      component="main"
      sx={{
        flexGrow: 1,
        p: 2.5,
        border: "1px solid rgba(0, 0, 0, 0.12)",
        borderRadius: "8px",
        my: 4,
      }}
    >
      <Box display="flex" alignItems="center" mb={3} justifyContent="space-between" width="100%">
        <Typography variant="body2" color="text.secondary">
          RM % should add up to 100. Certified &amp; Allocated Weight are auto-calculated.
        </Typography>
        {!isReturn && (
          <StyledButton
            disabled={isRestrictPopUpSave || isTotalOneHundred()}
            onClick={handleAddNewRow}
            sx={{ mb: 0 }}
            title={
              isTotalOneHundred()
                ? "Total percentage has reached 100%"
                : "Add new raw material entry"
            }
          >
            Add Raw Material
          </StyledButton>
        )}
      </Box>
      <EditableTable
        data={data}
        columns={columns}
        onSaveRow={handleSaveRow}
        onCellChange={handleCellChange}
        editingRowIndex={editingRowIndex}
        onEditRow={handleEditRow}
        onCancelEdit={handleCancelEdit}
        onDeleteRow={handleDeleteRow}
        hideEdit={isReturn}
        hideDelete={isReturn}
        enableEditing={!isReturn}
      />
    </Box>
  );
};

export default ComponentRawMaterialInfo;
